import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../styles/GlobalStyles';

const FooterContainer = styled.footer`
  background: ${theme.colors.surface};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: ${theme.spacing.xl} 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.md};
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
    gap: ${theme.spacing.lg};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const FooterTitle = styled.h3`
  color: ${theme.colors.text};
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: ${theme.spacing.md};
`;

const FooterText = styled.p`
  color: ${theme.colors.textSecondary};
  line-height: 1.6;
  font-size: 0.95rem;
`;

const FooterLink = styled(motion.a)`
  color: ${theme.colors.textSecondary};
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 0.95rem;

  &:hover {
    color: ${theme.colors.primary};
  }
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.md};
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.mobile}) {
    justify-content: center;
  }
`;

const SocialLink = styled(motion.a)`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text};
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    background: ${theme.colors.primary};
    color: white;
    transform: translateY(-2px);
    border-color: ${theme.colors.primary};
  }
`;

const BottomBar = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: ${theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${theme.colors.textSecondary};
  font-size: 0.9rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${theme.spacing.md};
    text-align: center;
  }
`;

const Copyright = styled.div`
  color: ${theme.colors.textSecondary};
`;

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <Container>
        <FooterContent>
          <FooterSection>
            <Logo>Simon.mba</Logo>
            <FooterText>
              Software engineer and entrepreneur passionate about building innovative solutions 
              that make a difference. Let's create something amazing together.
            </FooterText>
            <SocialLinks>
              <SocialLink
                href="https://github.com/smkohnstamm"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                📱
              </SocialLink>
              <SocialLink
                href="https://linkedin.com/in/smkohnstamm"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                💼
              </SocialLink>
              <SocialLink
                href="https://twitter.com/smkohnstamm"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                🐦
              </SocialLink>
            </SocialLinks>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Quick Links</FooterTitle>
            <FooterLink href="#about">About</FooterLink>
            <FooterLink href="#experience">Experience</FooterLink>
            <FooterLink href="#projects">Projects</FooterLink>
            <FooterLink href="#contact">Contact</FooterLink>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Projects</FooterTitle>
            <FooterLink href="https://dry.tennis" target="_blank" rel="noopener noreferrer">
              Dry Tennis
            </FooterLink>
            <FooterLink href="#projects">Portfolio</FooterLink>
            <FooterLink href="#projects">GitHub</FooterLink>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Contact</FooterTitle>
            <FooterLink href="mailto:simon@simon.mba">simon@simon.mba</FooterLink>
            <FooterLink href="#contact">Get in Touch</FooterLink>
            <FooterLink href="#contact">Resume</FooterLink>
          </FooterSection>
        </FooterContent>

        <BottomBar>
          <Copyright>
            © {currentYear} Simon Kohnstamm. All rights reserved.
          </Copyright>
          <div>
            Built with ❤️ using React & AWS CDK
          </div>
        </BottomBar>
      </Container>
    </FooterContainer>
  );
};

export default Footer;
